// FaceLock Ink Scan lock-on: WHERE the mark is in the look copy, from colour
// pixels alone (owner, 2026-10-07: on his phone the scan rushed and kept
// pictures with no tattoo in them; it must locate the tattoo and lock on to
// it). The page hands over the small colour copy it already reads several
// times a second (InkScan's look, about 180 x 320); this finds the mark the
// person named, or says plainly that there is none (or no skin at all).
//
// How, in order (each step says why; the numbers were measured on synthetic
// scenes of skin, ink, hair, shadows, walls and shirts, 2026-10-07):
//   1. skin: a loose colour test (chromaticity, so brightness does not
//      matter), then THIS person's skin in THIS light: the median colour of
//      the skin-like centre, plus or minus its own spread. A wall or a white,
//      grey or blue shirt ends here ("noskin"): that is what was taken before;
//   2. the local skin level: per 16 px cell a high percentile of its skin,
//      then the 3rd lowest of the 3 x 3 cells around, so a shadow edge takes
//      the dark side's level and never shows as ink;
//   3. ink: darker than the skin around it and not redder than skin (ink is
//      neutral or blue-black; folds, lips and nostrils are redder), or a
//      colour that is not skin (colour ink, faded ink, very dark skin). The
//      cut-off tightens on textured skin (hair), never loosens;
//   4. thick ink only (3 of 8 neighbours): a hair, a pore or noise is not
//      (and a frame with much thin ink is hairy: see TEX);
//   5. strokes grouped on 6 px cells; a group is not the mark when it is too
//      small, at the frame edge, too faint, the wrong shape, an eye (white
//      sclera in it), a solid area running off the frame (a sleeve), a web of
//      hair (texture), or not on skin;
//   6. the mark: the strongest group near the centre, held on the last lock
//      (so the box does not hop between marks) and on the named place (the
//      face model's anchor, ink-face.ts).
//
// A pixel the face model masks (FindOpts.mask) is never ink, nor an eye's white.
// The look copy must be an area-averaged downscale (the canvas'
// imageSmoothingQuality "high"): with a plain bilinear one, sparse arm hair
// passed the thickness rule as a tattoo in 5 of 6 looks.
//
// Boxes are fractions of the UNMIRRORED look frame (the front camera preview
// is mirrored on screen only). One finder per frame size keeps every buffer
// between looks: a look makes no new buffers, only its small result.

export type MarkKind = "tattoo" | "scar" | "mole" | "birthmark";

/** A box in fractions (0..1) of the UNMIRRORED look frame. */
export interface Box01 {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** What the mark looks like, to tell the same mark from another (close_b against close_a). */
export interface InkSignature {
  /** Ink brightness against the skin around it (0 black .. 1 like the skin). */
  dark: number;
  /** Ink chromaticity minus the skin's (red, green). */
  dr: number;
  dg: number;
  /** Share of the mark's box that is ink. */
  fill: number;
}

export interface InkFind {
  found: boolean;
  /** Why nothing was found: no skin in view, no ink at all, or ink that is not a mark. */
  why: "noskin" | "none" | "weak" | null;
  box: Box01 | null;
  /** The ink-weighted centre, fractions of the frame. */
  centre: { x: number; y: number } | null;
  /** Box area over frame area. */
  share: number;
  /** Box long side over the frame's SHORT side: how much of the frame the mark fills. */
  span: number;
  /** Mean ink strength (0..1). */
  contrast: number;
  /** Share of the box that is ink. */
  solidity: number;
  /** Skin share of the central 84% of the frame. */
  skin: number;
  /** The skin's 60th percentile brightness, 0..1 (very dark skin is not a dark room). */
  skinY: number;
  /** Skin share inside FindOpts.guide (0 without a guide): the scar gate. */
  skinBox: number;
  /** The frame is hairy (a group was a web of hair, or much of the centre is thin ink): thin marks are not trusted here. */
  hairy: boolean;
  sig: InkSignature | null;
}

export interface FindOpts {
  /** A face, neck or head place: smaller marks count, and eyes and lips are not ink. */
  face?: boolean;
  /** How far the view has relaxed (InkJudge.stage): 0 from the start, 1 from 12 s, 2 from 20 s. */
  stage?: 0 | 1 | 2;
  /** The box locked on in the last look: the same mark is kept. */
  prev?: Box01 | null;
  /** Where the named place is (the face model's anchor): a mark there wins. */
  hint?: Box01 | null;
  /** Look-size mask, 1 = never ink (eyes, brows, lips, nostrils, off the face: ink-face.ts). */
  mask?: Uint8Array | null;
  /** The guide box on screen: its skin share is FindOpts' skinBox. */
  guide?: Box01 | null;
}

export type MarkFinder = (rgba: Uint8ClampedArray, kind?: MarkKind, o?: FindOpts) => InkFind;

type Stage3 = readonly [number, number, number];
interface KindRule {
  /** Ink is darker than this share of the skin level, per stage. */
  dark: Stage3;
  /** Least chromaticity distance from skin for colour ink, per stage. */
  colour: Stage3;
  /** Least mean ink strength of a group, per stage. */
  contrast: Stage3;
  /** Least ink pixels, share of the frame (minPxFace on the face). */
  minPx: number;
  minPxFace: number;
  /** Shape: least solidity, most aspect (long side / short side). */
  solidity: number;
  aspect: number;
  /** Dark ink may be at most this much redder than skin (tattoo only). */
  notRedder: number;
  /** Brighter than this share of the skin level is a mark too (pale scars only). */
  light: number;
}

export const LOCK = {
  /** The local skin level: cell size, the percentile of its skin, which of the 3 x 3 sorted cells. */
  levelCell: 16,
  levelPct: 0.75,
  levelNbr: 2,
  /** Stroke grouping: cell size and the ink pixels that make a cell count. */
  cell: 6,
  cellMin: 3,
  /** A group whose centre is in this outer band of the frame is not the mark (clothing edges, the e2e red corner). */
  margin: 0.08,
  /** Skin in the central 84% below this: no skin ("noskin"). */
  minSkin: 0.12,
  /** Skin among the non-ink pixels around a group (its box grown 25%): it is on skin. */
  ctxSkin: 0.5,
  /** Ink must stand this many skin-texture sigmas out of the skin. */
  noiseK: 4.5,
  /** Ink neighbours (of 8) an ink pixel needs: a hair or a pore has fewer. */
  thick: 3,
  /** Face only: a group with this share of white, neutral sclera pixels in its box is an eye. */
  sclera: 0.015,
  /** A group touching the frame edge this solid and this big is a sleeve, hair or background. */
  solidEdge: 0.6,
  solidEdgeShare: 0.12,
  kinds: {
    tattoo: { dark: [0.72, 0.76, 0.8], colour: [0.055, 0.048, 0.042], contrast: [0.22, 0.18, 0.15], minPx: 0.0008, minPxFace: 0.0003, solidity: 0, aspect: 99, notRedder: 0.03, light: Infinity },
    mole: { dark: [0.8, 0.84, 0.87], colour: [0.05, 0.05, 0.05], contrast: [0.16, 0.13, 0.11], minPx: 0.0002, minPxFace: 0.0002, solidity: 0.35, aspect: 2.6, notRedder: Infinity, light: Infinity },
    birthmark: { dark: [0.88, 0.9, 0.92], colour: [0.035, 0.035, 0.035], contrast: [0.09, 0.07, 0.06], minPx: 0.0015, minPxFace: 0.0015, solidity: 0.25, aspect: 4, notRedder: Infinity, light: Infinity },
    scar: { dark: [0.86, 0.86, 0.86], colour: [0.035, 0.035, 0.035], contrast: [0.08, 0.08, 0.08], minPx: 0.0008, minPxFace: 0.0008, solidity: 0, aspect: 99, notRedder: Infinity, light: 1.08 },
  } as Record<MarkKind, KindRule>,
} as const;

/**
 * A web of hair is not a tattoo (2026-10-07: a dense hairy forearm locked and
 * was taken at 13 s). A thin group (solidity under maxSolidity) of at least
 * minW x minH px whose 3 x 4 tiles are all about as inky (the emptiest at
 * least minUniform of the second fullest) is texture, and the frame is
 * "hairy": then any group thinner than hairyMinSolidity is not trusted
 * either. A group no denser than the ring around it (ringRatio, when the ring
 * is at least ringArea of its box) is texture too. These never relax.
 * Against the second fullest tile, not the fullest: once the relaxed cut-off
 * joins the hairs into one web that also takes in a solid patch at the frame
 * edge (a sleeve, a strap; the e2e red corner), that one full tile hid the
 * web, and the hairy arm was taken at 21 s; no mark was lost by the change.
 * Even tiles alone are not hair: hair runs on past the group, a dot pattern
 * (dotwork, a dot mandala, a halftone fill) stops at its edge on bare skin.
 * So the even group is texture only when its box reaches the frame's edge
 * band or the ring round it (its box grown 25%) holds ink (at least webRing);
 * a sparse even dot field was dropped at every stage and never locked
 * (2026-10-07 review).
 * A frame is hairy too when much of its centre is thin ink (thinShare: ink
 * that fails the thickness rule, a strand on its own): a dense hairy arm is
 * 0.05 and more, every mark scene 0.044 at most (a faded wash; fine line
 * 0.03). In a hairy frame a group with less than hairyInk ink px (inside the
 * edge band) per px of its long side is a clump or a web of crossing strands,
 * not a mark: hair is 1 to 2.5 there, the thinnest mark (fine line) 3.8, a
 * mole 4.3 and up. The
 * same hairy arm moved so its densest strands sat mid-frame locked and was
 * taken at 14 s, the face's and the wide view's size rules being the only
 * ones left once the rules relaxed (2026-10-07 review).
 * And in ANY frame: a group whose ink is mostly thin strands (thick ink under
 * localThick of all the ink in its cells) and that holds under localInk thick
 * px per px of its long side is hairs crossing, not a mark. A lightly hairy
 * arm (150 to 250 strands) is not hairy enough for thinShare at the strictest
 * stage, yet a few crossings mid-frame locked and were taken by themselves in
 * the browser (2026-10-08 e2e): those groups are 0.41 to 0.89 thick and 0.8
 * to 2.9 per px; every mark scene that is mostly strands (fine line, small
 * and far tattoos, faded ink) is 3.9 per px and up, and a small solid mark (a
 * mole, a dot) is all thick ink (1.0). And a group thinner than webSolidity
 * reaching past webSpan of the frame's short side is a web of hair joined up
 * by the relaxed cut-off (0.053 to 0.060, 0.74 to 0.78 of the frame); the
 * sparsest mark that wide, once relaxed, is a fine-line piece at 0.079 (at the
 * strictest stage it is 0.043, so the rule waits for the relax, as the webs
 * do). Over 2,880 lightly
 * hairy looks (150 to 250 strands, 40 arms, every stage, face and body rules)
 * one group is still found, at the last stage only: a mid-size clump (0.16 of
 * the frame) that the close-ups' size rule turns away.
 */
export const TEX = {
  maxSolidity: 0.12, minUniform: 0.2, minW: 12, minH: 16, hairyMinSolidity: 0.06, ringArea: 0.3, ringRatio: 2, webRing: 0.01,
  thinShare: 0.035, hairyInk: 3, localThick: 0.95, localInk: 3.4, webSolidity: 0.07, webSpan: 0.6,
} as const;

/** The bin of a histogram where the running count reaches q of total. */
function pct(hist: Uint32Array, total: number, q: number): number {
  const target = q * total;
  let acc = 0;
  for (let i = 0; i < hist.length; i++) {
    acc += hist[i];
    if (acc >= target) return i;
  }
  return hist.length - 1;
}

export function iou(a: Box01, b: Box01): number {
  return iouOf(a.x, a.y, a.w, a.h, b);
}

function iouOf(x: number, y: number, w: number, h: number, b: Box01): number {
  const x0 = Math.max(x, b.x);
  const y0 = Math.max(y, b.y);
  const x1 = Math.min(x + w, b.x + b.w);
  const y1 = Math.min(y + h, b.y + b.h);
  const i = Math.max(0, x1 - x0) * Math.max(0, y1 - y0);
  const u = w * h + b.w * b.h - i;
  return u > 0 ? i / u : 0;
}

/** Same mark again (close_b against close_a): ink colour and darkness alike, fill alike. */
export function sameMark(a: InkSignature, b: InkSignature): boolean {
  if (!a || !b) return true;
  const dc = Math.hypot(a.dr - b.dr, a.dg - b.dg);
  const fr = a.fill / Math.max(1e-6, b.fill);
  return dc <= 0.03 && Math.abs(a.dark - b.dark) <= 0.15 && fr >= 0.6 && fr <= 1.67;
}

/** A finder for looks of w x h px. Every buffer is made here, once; a look reuses them. */
export function createMarkFinder(w: number, h: number): MarkFinder {
  const n = w * h;
  const Y = new Float32Array(n);
  const RN = new Float32Array(n);
  const GN = new Float32Array(n);
  const skin = new Uint8Array(n);
  const raw = new Uint8Array(n);
  const ink = new Uint8Array(n);
  const scl = new Uint8Array(n);
  const con = new Float32Array(n);
  const LC = LOCK.levelCell;
  const lw = Math.ceil(w / LC);
  const lh = Math.ceil(h / LC);
  const ln = lw * lh;
  const lcH = new Uint16Array(ln * 32);
  const lcN = new Uint16Array(ln);
  const lcV = new Float32Array(ln);
  const lv = new Float32Array(ln);
  const lvInv = new Float32Array(ln);
  const C = LOCK.cell;
  const cw = Math.ceil(w / C);
  const ch = Math.ceil(h / C);
  const cn = cw * ch;
  const cInk = new Uint16Array(cn);
  const cThin = new Uint16Array(cn);
  const cCon = new Float32Array(cn);
  const cScl = new Uint16Array(cn);
  const cX0 = new Int16Array(cn);
  const cY0 = new Int16Array(cn);
  const cX1 = new Int16Array(cn);
  const cY1 = new Int16Array(cn);
  const cDr = new Float32Array(cn);
  const cDg = new Float32Array(cn);
  const cDk = new Float32Array(cn);
  const inky = new Uint8Array(cn);
  const dil = new Uint8Array(cn);
  const lab = new Int32Array(cn);
  const stack = new Int32Array(cn);
  // The groups (at most one per cell): pixels, contrast, centre sums, exact pixel bounds, sclera, colour, darkness, texture.
  const gPx = new Int32Array(cn);
  const gCon = new Float64Array(cn);
  const gSx = new Float64Array(cn);
  const gSy = new Float64Array(cn);
  const gX0 = new Int32Array(cn);
  const gY0 = new Int32Array(cn);
  const gX1 = new Int32Array(cn);
  const gY1 = new Int32Array(cn);
  const gScl = new Int32Array(cn);
  const gDr = new Float64Array(cn);
  const gDg = new Float64Array(cn);
  const gDk = new Float64Array(cn);
  const gTex = new Uint8Array(cn);
  const gThin = new Int32Array(cn);
  const colL = new Int16Array(w);
  const colC = new Int16Array(w);
  for (let x = 0; x < w; x++) {
    colL[x] = (x / LC) | 0;
    colC[x] = (x / C) | 0;
  }
  const hr = new Uint32Array(256);
  const hg = new Uint32Array(256);
  const hy = new Uint32Array(256);
  const hn = new Uint32Array(200);
  const nb = new Float32Array(9);
  const mx0 = Math.floor(w * LOCK.margin);
  const mx1 = Math.ceil(w * (1 - LOCK.margin));
  const my0 = Math.floor(h * LOCK.margin);
  const my1 = Math.ceil(h * (1 - LOCK.margin));
  const short = Math.min(w, h);

  /** Ink density of the box [ax, bx) x [ay, by). */
  const density = (ax: number, bx: number, ay: number, by: number) => {
    let s = 0;
    let k = 0;
    for (let y = ay; y < by; y++) for (let x = ax, i = y * w + ax; x < bx; x++, i++) { s += ink[i]; k++; }
    return k ? s / k : 0;
  };

  return function find(rgba: Uint8ClampedArray, kind: MarkKind = "tattoo", o: FindOpts = {}): InkFind {
    const K = LOCK.kinds[kind] ?? LOCK.kinds.tattoo;
    const st = o.stage === 1 || o.stage === 2 ? o.stage : 0;
    const face = !!o.face;
    const mask = o.mask && o.mask.length === n ? o.mask : null;
    const guide = o.guide ?? null;
    const gx0 = guide ? Math.max(0, Math.floor(guide.x * w)) : 0;
    const gx1 = guide ? Math.min(w, Math.ceil((guide.x + guide.w) * w)) : 0;
    const gy0 = guide ? Math.max(0, Math.floor(guide.y * h)) : 0;
    const gy1 = guide ? Math.min(h, Math.ceil((guide.y + guide.h) * h)) : 0;
    const guideN = Math.max(0, gx1 - gx0) * Math.max(0, gy1 - gy0);
    hr.fill(0);
    hg.fill(0);
    hy.fill(0);

    // 1. luma, chromaticity, a generous skin test; skin colour histograms over the central area
    let nPrior = 0;
    let nArea = 0;
    for (let y = 0, i = 0, p = 0; y < h; y++) {
      const inY = y >= my0 && y < my1;
      for (let x = 0; x < w; x++, i++, p += 4) {
        const r = rgba[p];
        const g = rgba[p + 1];
        const b = rgba[p + 2];
        const s = r + g + b;
        const yy = 0.299 * r + 0.587 * g + 0.114 * b;
        Y[i] = yy;
        let pr = 0;
        if (s >= 45) {
          const k = 1 / s;
          const rn = r * k;
          const gn = g * k;
          RN[i] = rn;
          GN[i] = gn;
          // The lightest skin (Monk scale MST-1, #f6ede4) is barely redder than blue: r - b at least 0.03 of the sum and
          // a red share of 0.345 called it "no skin", tattoo or scar alike, and a 2% cooler white balance did the same
          // to the next tones (2026-10-07 review). White, grey and blue walls and shirts still fail it (r - b near 0).
          if (s >= 60 && r > g && r - b >= 0.015 * s && rn >= 0.338 && rn <= 0.62 && gn >= 0.24 && gn <= 0.38 && yy < 250) pr = 1;
        } else {
          RN[i] = -1;
          GN[i] = -1;
        }
        skin[i] = pr;
        if (inY && x >= mx0 && x < mx1) {
          nArea++;
          if (pr) {
            nPrior++;
            hr[(RN[i] * 255) | 0]++;
            hg[(GN[i] * 255) | 0]++;
            hy[yy | 0]++;
          }
        }
      }
    }
    const skinShare = nPrior / Math.max(1, nArea);
    const skinY = nPrior ? pct(hy, nPrior, 0.6) / 255 : 0;
    if (skinShare < LOCK.minSkin) {
      let gs = 0;
      for (let y = gy0; y < gy1; y++) for (let x = gx0, i = y * w + gx0; x < gx1; x++, i++) gs += skin[i];
      return miss("noskin", skinShare, skinY, guideN ? gs / guideN : 0, false);
    }

    // 2. this person's skin, in this light: median chromaticity plus or minus a robust spread
    const mr = pct(hr, nPrior, 0.5) / 255;
    const mg = pct(hg, nPrior, 0.5) / 255;
    const sdr = (pct(hr, nPrior, 0.75) - pct(hr, nPrior, 0.25)) / 255 / 1.35;
    const sdg = (pct(hg, nPrior, 0.75) - pct(hg, nPrior, 0.25)) / 255 / 1.35;
    const tolR = Math.max(0.03, 3.5 * sdr);
    const tolG = Math.max(0.02, 3.5 * sdg);
    const gy = pct(hy, nPrior, 0.6);
    const yFloor = 0.4 * gy;
    lcH.fill(0);
    lcN.fill(0);
    let guideSkin = 0;
    for (let y = 0, i = 0; y < h; y++) {
      const crow = ((y / LC) | 0) * lw;
      const inGy = y >= gy0 && y < gy1;
      for (let x = 0; x < w; x++, i++) {
        if (!skin[i]) continue;
        const dr = RN[i] - mr;
        const dg = GN[i] - mg;
        if (dr > tolR || dr < -tolR || dg > tolG || dg < -tolG) { skin[i] = 0; continue; }
        if (inGy && x >= gx0 && x < gx1) guideSkin++;
        if (Y[i] >= yFloor) {
          const c = crow + colL[x];
          lcH[c * 32 + (Y[i] >> 3)]++;
          lcN[c]++;
        }
      }
    }
    const skinBox = guideN ? guideSkin / guideN : 0;

    // 3. the local skin level: per cell a high percentile of its skin, then the 3rd lowest of the 3 x 3 cells
    const need = LC * LC * 0.2;
    for (let c = 0; c < ln; c++) {
      lcV[c] = -1;
      if (lcN[c] < need) continue;
      const tgt = lcN[c] * LOCK.levelPct;
      let acc = 0;
      for (let b = 0, off = c * 32; b < 32; b++) {
        acc += lcH[off + b];
        if (acc >= tgt) { lcV[c] = b * 8 + 4; break; }
      }
    }
    for (let cy = 0; cy < lh; cy++) {
      for (let cx = 0; cx < lw; cx++) {
        let k = 0;
        for (let yy = Math.max(0, cy - 1); yy <= Math.min(lh - 1, cy + 1); yy++) {
          for (let xx = Math.max(0, cx - 1); xx <= Math.min(lw - 1, cx + 1); xx++) {
            const v = lcV[yy * lw + xx];
            if (v < 0) continue;
            let j = k++; // insertion sort into nb[0..k)
            while (j > 0 && nb[j - 1] > v) { nb[j] = nb[j - 1]; j--; }
            nb[j] = v;
          }
        }
        lv[cy * lw + cx] = k ? nb[Math.min(LOCK.levelNbr, k - 1)] : -1;
      }
    }
    // Cells with no skin (solid ink) take their neighbours' level, a ring at a time.
    for (let pass = 0; pass < 8; pass++) {
      let missing = 0;
      for (let c = 0; c < ln; c++) {
        if (lv[c] !== -1) continue;
        const cx = c % lw;
        const cy = (c / lw) | 0;
        let s = 0;
        let k = 0;
        for (let yy = Math.max(0, cy - 1); yy <= Math.min(lh - 1, cy + 1); yy++) {
          for (let xx = Math.max(0, cx - 1); xx <= Math.min(lw - 1, cx + 1); xx++) {
            const v = lv[yy * lw + xx];
            if (v >= 0) { s += v; k++; }
          }
        }
        if (k) lv[c] = -2 - s / k;
        else missing++;
      }
      for (let c = 0; c < ln; c++) if (lv[c] <= -2) lv[c] = -lv[c] - 2;
      if (!missing) break;
    }
    for (let c = 0; c < ln; c++) {
      if (lv[c] < 0) lv[c] = gy;
      lvInv[c] = 1 / Math.max(8, lv[c]);
    }

    // 4. skin texture: a robust sigma of luma over its level on skin; ink must stand LOCK.noiseK sigmas out
    hn.fill(0);
    let nn = 0;
    for (let y = 1; y < h; y += 2) {
      const crow = ((y / LC) | 0) * lw;
      for (let x = 1, i = y * w + 1; x < w; x += 2, i += 2) {
        if (!skin[i] || Y[i] < yFloor) continue;
        const v = Y[i] * lvInv[crow + colL[x]];
        hn[v >= 1.995 ? 199 : Math.round(v * 100)]++;
        nn++;
      }
    }
    const sigma = nn ? (pct(hn, nn, 0.75) - pct(hn, nn, 0.25)) / 100 / 1.35 : 0.05;
    const tDark = Math.min(K.dark[st], 1 - LOCK.noiseK * sigma);
    const tCol = Math.max(K.colour[st], 5 * Math.max(sdr, sdg));
    const tCol2 = tCol * tCol;
    const notRedder = K.notRedder;
    const light = K.light;

    // 5. raw ink: darker than the skin around it (and not redder), or a colour that is not skin; masked pixels never
    for (let y = 0, i = 0; y < h; y++) {
      const crow = ((y / LC) | 0) * lw;
      for (let x = 0; x < w; x++, i++) {
        if (mask && mask[i] === 1) { raw[i] = 0; con[i] = 0; scl[i] = 0; continue; }
        const d = Y[i] * lvInv[crow + colL[x]];
        const rn = RN[i];
        let hit = 0;
        let c = 0;
        if (face) scl[i] = Y[i] >= 140 && d >= 1.1 && rn > 0.298 && rn < 0.368 && GN[i] > 0.303 && GN[i] < 0.363 ? 1 : 0;
        if (d < tDark && rn - mr <= notRedder) { hit = 1; c = 1 - d; }
        else if (!skin[i] && rn >= 0 && Y[i] < 245 && d < 1.08) {
          const dr = rn - mr;
          const dg = GN[i] - mg;
          const dist2 = dr * dr + dg * dg;
          // On the face a colour redder than skin is lips, a flush or a pimple, not ink.
          if (dist2 >= tCol2 && !(face && dr > 0 && dg < 0.01)) { hit = 1; c = Math.min(1, Math.sqrt(dist2) * 3); }
        } else if (d > light && Y[i] < 245 && rn >= 0) { hit = 1; c = d - 1; }
        raw[i] = hit;
        con[i] = c;
      }
    }

    // 6. thick ink only (a hair, a pore, noise are not); per grouping cell: count, contrast, bounds, colour
    cInk.fill(0);
    cThin.fill(0);
    cCon.fill(0);
    cScl.fill(0);
    cDr.fill(0);
    cDg.fill(0);
    cDk.fill(0);
    cX0.fill(32767);
    cY0.fill(32767);
    cX1.fill(-1);
    cY1.fill(-1);
    ink.fill(0);
    // Thin ink in the central area (strands on their own): how hairy the frame is.
    let thinN = 0;
    for (let y = 1; y < h - 1; y++) {
      const crow = ((y / C) | 0) * cw;
      const lrow = ((y / LC) | 0) * lw;
      const inY = y >= my0 && y < my1;
      for (let x = 1, i = y * w + 1; x < w - 1; x++, i++) {
        if (face && scl[i]) cScl[crow + colC[x]]++;
        if (!raw[i]) continue;
        const k = raw[i - w - 1] + raw[i - w] + raw[i - w + 1] + raw[i - 1] + raw[i + 1] + raw[i + w - 1] + raw[i + w] + raw[i + w + 1];
        if (k < LOCK.thick) {
          if (inY && x >= mx0 && x < mx1) thinN++;
          cThin[crow + colC[x]]++;
          continue;
        }
        ink[i] = 1;
        const c = crow + colC[x];
        cInk[c]++;
        cCon[c] += con[i];
        if (RN[i] >= 0) { cDr[c] += RN[i] - mr; cDg[c] += GN[i] - mg; }
        cDk[c] += Y[i] * lvInv[lrow + colL[x]];
        if (x < cX0[c]) cX0[c] = x;
        if (x > cX1[c]) cX1[c] = x;
        if (y < cY0[c]) cY0[c] = y;
        if (y > cY1[c]) cY1[c] = y;
      }
    }

    // 7. group strokes: inky cells, grown by one cell, 8-connected
    for (let c = 0; c < cn; c++) inky[c] = cInk[c] >= LOCK.cellMin ? 1 : 0;
    dil.fill(0);
    for (let cy = 0; cy < ch; cy++) {
      for (let cx = 0; cx < cw; cx++) {
        if (!inky[cy * cw + cx]) continue;
        for (let yy = Math.max(0, cy - 1); yy <= Math.min(ch - 1, cy + 1); yy++) {
          for (let xx = Math.max(0, cx - 1); xx <= Math.min(cw - 1, cx + 1); xx++) dil[yy * cw + xx] = 1;
        }
      }
    }
    lab.fill(-1);
    let groups = 0;
    for (let c0 = 0; c0 < cn; c0++) {
      if (!dil[c0] || lab[c0] >= 0) continue;
      const gi = groups;
      gPx[gi] = 0; gCon[gi] = 0; gSx[gi] = 0; gSy[gi] = 0; gScl[gi] = 0; gDr[gi] = 0; gDg[gi] = 0; gDk[gi] = 0; gTex[gi] = 0; gThin[gi] = 0;
      gX0[gi] = 1e9; gY0[gi] = 1e9; gX1[gi] = -1; gY1[gi] = -1;
      let sp = 0;
      stack[sp++] = c0;
      lab[c0] = gi;
      while (sp) {
        const c = stack[--sp];
        const cx = c % cw;
        const cy = (c / cw) | 0;
        gScl[gi] += cScl[c];
        gThin[gi] += cThin[c];
        if (inky[c]) {
          const k = cInk[c];
          gPx[gi] += k;
          gCon[gi] += cCon[c];
          gDr[gi] += cDr[c];
          gDg[gi] += cDg[c];
          gDk[gi] += cDk[c];
          gSx[gi] += (cX0[c] + cX1[c] + 1) * 0.5 * k;
          gSy[gi] += (cY0[c] + cY1[c] + 1) * 0.5 * k;
          if (cX0[c] < gX0[gi]) gX0[gi] = cX0[c];
          if (cY0[c] < gY0[gi]) gY0[gi] = cY0[c];
          if (cX1[c] + 1 > gX1[gi]) gX1[gi] = cX1[c] + 1;
          if (cY1[c] + 1 > gY1[gi]) gY1[gi] = cY1[c] + 1;
        }
        for (let yy = Math.max(0, cy - 1); yy <= Math.min(ch - 1, cy + 1); yy++) {
          for (let xx = Math.max(0, cx - 1); xx <= Math.min(cw - 1, cx + 1); xx++) {
            const q = yy * cw + xx;
            if (dil[q] && lab[q] < 0) { lab[q] = gi; stack[sp++] = q; }
          }
        }
      }
      if (gPx[gi]) groups++;
    }

    // 8. judge the groups. First the texture pass: a thin group whose 3 x 4 tiles are all about as inky is a web of hair.
    const minPx = Math.max(6, (face ? K.minPxFace : K.minPx) * n);
    let hairy = thinN >= TEX.thinShare * nArea;
    for (let gi = 0; gi < groups; gi++) {
      const bw = gX1[gi] - gX0[gi];
      const bh = gY1[gi] - gY0[gi];
      const solidity = gPx[gi] / Math.max(1, bw * bh);
      if (!(gPx[gi] >= minPx && solidity < TEX.maxSolidity && bw >= TEX.minW && bh >= TEX.minH)) continue;
      // The emptiest tile against the SECOND fullest: one solid patch merged into the web does not hide it.
      let lo = 1;
      let hi = 0;
      let hi2 = 0;
      for (let ty = 0; ty < 4; ty++) {
        for (let tx = 0; tx < 3; tx++) {
          const d = density(
            gX0[gi] + ((tx * bw / 3) | 0), gX0[gi] + (((tx + 1) * bw / 3) | 0),
            gY0[gi] + ((ty * bh / 4) | 0), gY0[gi] + (((ty + 1) * bh / 4) | 0),
          );
          if (d < lo) lo = d;
          if (d > hi) { hi2 = hi; hi = d; } else if (d > hi2) hi2 = d;
        }
      }
      if (!(hi2 > 0 && lo / hi2 >= TEX.minUniform)) continue;
      // Even, but does the web run on past it (hair), or stop at its edge on bare skin (a dot pattern)?
      const edge = gX0[gi] <= mx0 || gY0[gi] <= my0 || gX1[gi] >= mx1 || gY1[gi] >= my1;
      if (!edge) {
        const rx0 = Math.max(0, Math.floor(gX0[gi] - bw * 0.25));
        const rx1 = Math.min(w, Math.ceil(gX1[gi] + bw * 0.25));
        const ry0 = Math.max(0, Math.floor(gY0[gi] - bh * 0.25));
        const ry1 = Math.min(h, Math.ceil(gY1[gi] + bh * 0.25));
        let ringN = 0;
        let ringInk = 0;
        for (let y = ry0; y < ry1; y += 2) {
          for (let x = rx0, i = y * w + rx0; x < rx1; x += 2, i += 2) {
            if (x >= gX0[gi] && x < gX1[gi] && y >= gY0[gi] && y < gY1[gi]) continue;
            ringN++;
            ringInk += ink[i];
          }
        }
        if (!ringN || ringInk / ringN < TEX.webRing) continue;
      }
      gTex[gi] = 1;
      hairy = true;
    }
    const prev = o.prev ?? null;
    const hint = o.hint ?? null;
    let best = -1;
    let bestScore = 0;
    for (let gi = 0; gi < groups; gi++) {
      const px = gPx[gi];
      if (px < minPx) continue; // too small
      const cx = gSx[gi] / px;
      const cy = gSy[gi] / px;
      if (cx < mx0 || cx > mx1 || cy < my0 || cy > my1) continue; // at the edge
      const contrast = gCon[gi] / px;
      if (contrast < K.contrast[st]) continue; // too faint
      const x0 = gX0[gi];
      const y0 = gY0[gi];
      const x1 = gX1[gi];
      const y1 = gY1[gi];
      const bw = x1 - x0;
      const bh = y1 - y0;
      const area = Math.max(1, bw * bh);
      const solidity = px / area;
      const aspect = Math.max(bw, bh) / Math.max(1, Math.min(bw, bh));
      if (solidity < K.solidity || aspect > K.aspect) continue; // the wrong shape
      if (face && gScl[gi] / area >= LOCK.sclera) continue; // an eye
      const edges = +(x0 <= 1) + +(y0 <= 1) + +(x1 >= w - 1) + +(y1 >= h - 1);
      if (edges && solidity >= LOCK.solidEdge && area / n >= LOCK.solidEdgeShare) continue; // a solid area off the frame
      if (gTex[gi]) continue; // a web of hair
      // Mostly strands, and little thick ink for its length: hairs crossing, wherever the frame is not called hairy.
      if (px < TEX.localThick * (px + gThin[gi]) && px < TEX.localInk * Math.max(bw, bh)) continue;
      // A wide sparse web of strands, once the cut-off has relaxed: hair over most of the frame, not a mark.
      if (st > 0 && solidity < TEX.webSolidity && Math.max(bw, bh) > TEX.webSpan * short) continue;
      if (hairy && solidity < TEX.hairyMinSolidity) continue; // thin, in a hairy frame
      if (hairy) {
        // Little ink for its size, in a hairy frame: crossing strands. Only the ink inside the frame's edge band counts: a
        // solid patch at the edge (a sleeve, a strap, the e2e red corner) joined to a web of hair is not a mark either.
        let inner = 0;
        for (let y = Math.max(y0, my0); y < Math.min(y1, my1); y++) {
          const crow = ((y / C) | 0) * cw;
          for (let x = Math.max(x0, mx0), i = y * w + x; x < Math.min(x1, mx1); x++, i++) if (ink[i] && lab[crow + colC[x]] === gi) inner++;
        }
        if (inner < TEX.hairyInk * Math.max(bw, bh)) continue;
      }
      const ex0 = Math.max(0, Math.floor(x0 - bw * 0.25));
      const ex1 = Math.min(w, Math.ceil(x1 + bw * 0.25));
      const ey0 = Math.max(0, Math.floor(y0 - bh * 0.25));
      const ey1 = Math.min(h, Math.ceil(y1 + bh * 0.25));
      let nonInk = 0;
      let sk = 0;
      let ringN = 0;
      let ringInk = 0;
      for (let y = ey0; y < ey1; y += 2) {
        for (let x = ex0, i = y * w + ex0; x < ex1; x += 2, i += 2) {
          if (x < x0 || x >= x1 || y < y0 || y >= y1) { ringN++; ringInk += ink[i]; }
          if (ink[i]) continue;
          nonInk++;
          sk += skin[i];
        }
      }
      if (ringN * 4 >= TEX.ringArea * area && solidity < TEX.ringRatio * (ringInk / ringN)) continue; // no denser than around it
      if ((nonInk ? sk / nonInk : 0) < LOCK.ctxSkin) continue; // not on skin
      // 9. the strongest near the centre, held on the last lock and on the named place
      const dc = Math.hypot((cx - w / 2) / short, (cy - h / 2) / short);
      let score = px * contrast * Math.max(0.15, 1 - 1.2 * dc);
      const bx = x0 / w;
      const by = y0 / h;
      const bww = bw / w;
      const bhh = bh / h;
      if (prev) score *= 1 + 2 * iouOf(bx, by, bww, bhh, prev);
      if (hint && bx < hint.x + hint.w && hint.x < bx + bww && by < hint.y + hint.h && hint.y < by + bhh) score *= 3;
      if (best < 0 || score > bestScore) { best = gi; bestScore = score; }
    }
    if (best < 0) return miss(groups ? "weak" : "none", skinShare, skinY, skinBox, hairy);
    const px = gPx[best];
    const bw = gX1[best] - gX0[best];
    const bh = gY1[best] - gY0[best];
    const solidity = px / Math.max(1, bw * bh);
    const box = { x: gX0[best] / w, y: gY0[best] / h, w: bw / w, h: bh / h };
    return {
      found: true,
      why: null,
      box,
      centre: { x: gSx[best] / px / w, y: gSy[best] / px / h },
      share: box.w * box.h,
      span: Math.max(bw, bh) / short,
      contrast: gCon[best] / px,
      solidity,
      skin: skinShare,
      skinY: gy / 255,
      skinBox,
      hairy,
      sig: { dark: gDk[best] / px, dr: gDr[best] / px, dg: gDg[best] / px, fill: solidity },
    };
  };
}

function miss(why: "noskin" | "none" | "weak", skin: number, skinY: number, skinBox: number, hairy: boolean): InkFind {
  return { found: false, why, box: null, centre: null, share: 0, span: 0, contrast: 0, solidity: 0, skin, skinY, skinBox, hairy, sig: null };
}

const FINDERS = new Map<string, MarkFinder>();
/** At most this many frame sizes are kept (the look copy, the kept photo's re-check, a camera switch). */
const FINDERS_MAX = 4;

/** The finder for looks of w x h px, made once per size and kept. */
export function finderFor(w: number, h: number): MarkFinder {
  const key = `${w}x${h}`;
  let f = FINDERS.get(key);
  if (!f) {
    if (FINDERS.size >= FINDERS_MAX) FINDERS.delete(FINDERS.keys().next().value as string);
    f = createMarkFinder(w, h);
    FINDERS.set(key, f);
  }
  return f;
}
